/**
 * API client functions for canonical pieces management.
 */

import type {
  CanonicalPiece,
  CanonicalPieceDetails,
  GetCanonicalPieceDetailsResponse,
  LinkSuggestionInput,
  ListCanonicalPiecesResponse,
  PromoteCanonicalPieceInput,
  PromoteCanonicalPieceResponse,
  UpdateCanonicalPieceInput,
  UpdateCanonicalPieceResponse,
} from '../../types/canonical-piece'
import apiClient from './client'

/**
 * List all canonical pieces with suggestion counts.
 */
export async function listCanonicalPieces(): Promise<CanonicalPiece[]> {
  const response = await apiClient.get<ListCanonicalPiecesResponse>('/admin/canonical-pieces')
  return response.canonical_pieces
}

/**
 * Get canonical piece details with all linked suggestions.
 */
export async function getCanonicalPieceDetails(
  canonicalPieceId: string,
): Promise<CanonicalPieceDetails> {
  const response = await apiClient.get<GetCanonicalPieceDetailsResponse>(
    `/admin/canonical-pieces/${canonicalPieceId}`,
  )
  return {
    canonical_piece: response.canonical_piece,
    suggestions: response.suggestions,
  }
}

/**
 * Link a suggestion to a canonical piece.
 */
export async function linkSuggestionToCanonicalPiece(
  canonicalPieceId: string,
  suggestionId: string,
): Promise<void> {
  const input: LinkSuggestionInput = { suggestion_id: suggestionId }
  await apiClient.post(`/admin/canonical-pieces/${canonicalPieceId}/link`, input)
}

/**
 * Unlink a suggestion from a canonical piece.
 */
export async function unlinkSuggestionFromCanonicalPiece(
  canonicalPieceId: string,
  suggestionId: string,
): Promise<void> {
  await apiClient.delete(`/admin/canonical-pieces/${canonicalPieceId}/unlink/${suggestionId}`)
}

/**
 * Update a canonical piece.
 * Can be used to convert between freetext and Wikidata, or update existing data.
 */
export async function updateCanonicalPiece(
  canonicalPieceId: string,
  input: UpdateCanonicalPieceInput,
): Promise<UpdateCanonicalPieceResponse> {
  return await apiClient.put(`/admin/canonical-pieces/${canonicalPieceId}`, input)
}

/**
 * Promote a canonical piece to a Wikidata reference.
 * Sets the work_wikidata_id, regenerates the canonical piece ID,
 * and updates all linked suggestions.
 */
export async function promoteCanonicalPiece(
  canonicalPieceId: string,
  input: PromoteCanonicalPieceInput,
): Promise<PromoteCanonicalPieceResponse> {
  return await apiClient.post(`/admin/canonical-pieces/${canonicalPieceId}/promote`, input)
}
